/**
 * **habits** ads — domain layer (the ONLY app-specific part).
 *
 * The rest of the kit (`types`/`gate`/`caps`/`config`/`provider`/`placements`/
 * `rewards`) is generic and copied as-is from canon (`listto-app`, `876e3b27`);
 * this file is the habits row in the "plan per app" table in
 * `infra/guides/ADS_STRATEGY.md` §10:
 *
 *   tier **A** · breakpoints: *all of the day's habits completed* (`day-done`)
 *   and *weekly stats* · rewarded ⭐ **+1 streak protector** and **2× XP for 24 h**.
 *
 * habits is the portfolio's **canonical rewarded case**: "watch an ad →
 * +1 life" is Duolingo's mechanic, and here the streak IS the product, so the
 * affinity between the format and the value offered is maximal.
 *
 * ⚠️ **TWO DISTINCT ECONOMIES, intentionally:**
 *
 * | Reward | Where it lives | Why |
 * |---|---|---|
 * | `streak-freeze` | **server** (`streak_protector`) | it is a "life": if the client could mint one, a local `INSERT` would yield infinite protectors and the streak would stop meaning anything |
 * | `xp-boost` | **local** (`AdRewardsStore`) | XP is **recomputed** on the client from already-synced completions; there is nothing to falsify that the server could not rederive |
 *
 * Therefore the `streak-save` rewarded ad does NOT grant the protector: it asks
 * the server to grant it (`POST /api/habits/protectors/claim`, source
 * `rewarded_ad`), which applies ITS caps (inventory 3, 1/day) in addition to the
 * kit cap (3/day per type). The remaining local grant is a **receipt for the ad**,
 * never the inventory—the inventory is always read from synced rows.
 */

import type { DayKey } from "../gamification/types";
import { definePlacements } from "./placements";
import type { AdRewardsStore } from "./rewards";
import type { AdTier } from "./types";

/** habits ad tier (§9): consumption, all formats. */
export const HABITS_AD_TIER: AdTier = "A";

/** Reward types (stable ids: stored in local storage). */
export const REWARD_STREAK_FREEZE = "streak-freeze";
export const REWARD_XP_BOOST = "xp-boost";

/** XP multiplier for the temporary boost. */
export const XP_BOOST_MULTIPLIER = 2;
/** XP boost duration, in hours. */
export const XP_BOOST_HOURS = 24;

/**
 * Registry of habits placements. Source of truth: a breakpoint not listed here
 * cannot render an ad, and the test guard verifies that all are legal for tier A.
 */
export const HABITS_AD_PLACEMENTS = definePlacements({
  /**
   * ALL of today's scheduled habits completed. It is the product's only real
   * breather: completing ONE habit is **not** a breakpoint (that would place an
   * ad halfway through a task, exactly what §3 forbids).
   */
  "day-done": { kind: "interstitial", breakpoint: "done-screen" },
  /** Weekly stats: passive consumption ⇒ integrated native card, no interruption. */
  "weekly-stats": { kind: "native", breakpoint: "stats" },
  /** ⭐ "Save your streak": the Duolingo case. The SERVER grants it (see above). */
  "streak-save": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: { type: REWARD_STREAK_FREEZE, amount: 1 },
  },
  /** 2× XP for 24 h. Temporary by catalog: permanent is premium. */
  "xp-boost": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: {
      type: REWARD_XP_BOOST,
      amount: XP_BOOST_MULTIPLIER,
      durationHours: XP_BOOST_HOURS,
    },
  },
} as const);

export type HabitsPlacementId = keyof typeof HABITS_AD_PLACEMENTS;

const DAY_MS = 86_400_000;

function dayKeyOf(at: number): DayKey {
  const d = new Date(at);
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Local day-keys covered by a live XP boost.
 *
 * The gamification engine scores **per event**, and each event is a check-in
 * for a specific day; the boost, meanwhile, is a 24-hour wall-clock window. The
 * honest translation between them is "the days the window touches": a boost
 * redeemed at 22:00 doubles today's XP and tomorrow's until 22:00, and the day
 * is the finest granularity the event retains.
 *
 * It is bounded to the present: a live boost **does not double the future**
 * (it has not happened yet) or the past before redemption (that would rewrite
 * history, like a streak protector without a window).
 */
export function adXpBoostDays(store: AdRewardsStore, now: number = Date.now()): Set<DayKey> {
  const days = new Set<DayKey>();
  for (const grant of store.active(now)) {
    if (grant.type !== REWARD_XP_BOOST) {
      continue;
    }
    const until = Math.min(grant.expiresAt ?? now, now);
    for (let at = grant.grantedAt; at <= until; at += DAY_MS) {
      days.add(dayKeyOf(at));
    }
    days.add(dayKeyOf(until));
  }
  return days;
}

/** Is an XP boost live right now? (for the UI notice). */
export function hasXpBoost(store: AdRewardsStore, now: number = Date.now()): boolean {
  return store.hasUnlock(REWARD_XP_BOOST, now);
}

/**
 * When the latest live XP boost expires, or `null`.
 * The UI uses it to say HOW MUCH remains: a temporary benefit that does not say
 * when it ends feels like a benefit that never existed.
 */
export function xpBoostExpiresAt(store: AdRewardsStore, now: number = Date.now()): number | null {
  let latest: number | null = null;
  for (const grant of store.active(now)) {
    if (grant.type === REWARD_XP_BOOST && grant.expiresAt !== undefined) {
      latest = latest === null ? grant.expiresAt : Math.max(latest, grant.expiresAt);
    }
  }
  return latest;
}
